import React, { useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';

// ── Types ────────────────────────────────────────────────────────────
type Target = 'all' | 'sellers' | 'new_users';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

interface LastResult {
  count: number;
  target: string;
}

const TARGET_LABELS: Record<Target, string> = {
  all: '📢 All Users',
  sellers: '🏪 Sellers Only',
  new_users: '🆕 New Users (Last 30 Days)',
};

// ── Spinner ──────────────────────────────────────────────────────────
const Spinner: React.FC = () => (
  <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ── Toast Container ──────────────────────────────────────────────────
const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`
          flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border
          cursor-pointer min-w-[280px]
          ${toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
          }
        `}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
        <button className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    ))}
  </div>
);

// ── Confirm Dialog ───────────────────────────────────────────────────
interface ConfirmDialogProps {
  open: boolean;
  target: Target;
  sending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ open, target, sending, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="flex flex-col items-center text-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center">
            <span className="text-2xl">🔔</span>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Send Broadcast?</h3>
            <p className="text-sm text-gray-500 mt-1 leading-relaxed">
              You are about to send to{' '}
              <span className="font-semibold text-gray-700">{TARGET_LABELS[target]}</span> users.
              Are you sure?
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            disabled={sending}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600
              bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={sending}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white
              transition-all disabled:opacity-50 active:scale-95
              flex items-center justify-center gap-2"
            style={{ backgroundColor: sending ? '#063830' : '#085041' }}
            onMouseEnter={(e) => { if (!sending) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#063830'; }}
            onMouseLeave={(e) => { if (!sending) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#085041'; }}
          >
            {sending ? (
              <>
                <Spinner />
                Sending...
              </>
            ) : (
              'Send Now'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Phone Preview Card ───────────────────────────────────────────────
interface PreviewCardProps {
  title: string;
  body: string;
}

const PreviewCard: React.FC<PreviewCardProps> = ({ title, body }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 space-y-3">
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Preview</p>
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 space-y-2">
      {/* Notification header */}
      <div className="flex items-center gap-2.5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-white text-xs font-bold"
          style={{ backgroundColor: '#085041' }}
        >
          R
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs font-bold text-gray-700">Readora</span>
          <span className="text-gray-300 text-xs">·</span>
          <span className="text-xs text-gray-400">now</span>
        </div>
      </div>
      {/* Notification body */}
      <div className="pl-10">
        <p className="text-sm font-bold text-gray-800 leading-snug">
          {title || <span className="text-gray-300 font-normal italic">Your title here...</span>}
        </p>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
          {body || <span className="italic">Your message here...</span>}
        </p>
      </div>
    </div>
    <p className="text-xs text-gray-400 text-center">
      This is how your notification will appear on users' devices.
    </p>
  </div>
);

// ── Main Notifications Component ─────────────────────────────────────
const Notifications: React.FC = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState<Target>('all');
  const [sendPush, setSendPush] = useState(false);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [lastResult, setLastResult] = useState<LastResult | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toastIdRef = useRef(0);

  // ── Toast helpers ────────────────────────────────────────────────
  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Send broadcast ───────────────────────────────────────────────
  const handleSend = async () => {
    setSending(true);
    try {
      const res = await adminApi.post<{ count: number; target: string; message: string }>(
        '/admin/notifications/broadcast',
        { title: title.trim(), body: body.trim(), target, send_push: sendPush }
      );
      setLastResult({ count: res.data.count, target: TARGET_LABELS[target] });
      setTitle('');
      setBody('');
      setTarget('all');
      setSendPush(false);
      setShowConfirm(false);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to send notification.';
      addToast(detail, 'error');
      setShowConfirm(false);
    } finally {
      setSending(false);
    }
  };

  const canSend = title.trim().length > 0 && body.trim().length > 0;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">🔔 Broadcast Notification</h1>
        <p className="text-sm text-gray-500 mt-1">
          Send an in-app broadcast, with an optional OS push.
        </p>
      </div>

      {/* Success banner */}
      {lastResult && (
        <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl
          bg-emerald-50 border border-emerald-200 text-emerald-800">
          <span className="text-xl shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold">
              Sent to {lastResult.count.toLocaleString()} users successfully!
            </p>
            <p className="text-xs text-emerald-600 mt-0.5">
              Target: {lastResult.target}
            </p>
          </div>
          <button
            onClick={() => setLastResult(null)}
            className="ml-auto text-emerald-400 hover:text-emerald-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* LEFT — Form */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
          {/* Title */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-gray-700">
              Notification Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. New Feature Available!"
              value={title}
              maxLength={100}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50
                text-sm text-gray-800 placeholder-gray-400
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                transition-all duration-200"
            />
            <p className="text-xs text-gray-400 text-right">{title.length} / 100</p>
          </div>

          {/* Body */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-gray-700">
              Message <span className="text-red-400">*</span>
            </label>
            <textarea
              rows={4}
              placeholder="Write your notification message..."
              value={body}
              maxLength={300}
              onChange={(e) => setBody(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50
                text-sm text-gray-800 placeholder-gray-400 resize-none
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                transition-all duration-200"
            />
            <p className="text-xs text-gray-400 text-right">{body.length} / 300</p>
          </div>

          {/* Target */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-gray-700">
              Target Audience
            </label>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value as Target)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50
                text-sm text-gray-800
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                transition-all duration-200 cursor-pointer"
            >
              {(Object.entries(TARGET_LABELS) as [Target, string][]).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Optional OS push — default off and migration/worker gated */}
          <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 cursor-pointer">
            <input
              type="checkbox"
              checked={sendPush}
              onChange={(e) => setSendPush(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-teal-700"
            />
            <span>
              <span className="block text-sm font-semibold text-gray-700">Also send an OS push</span>
              <span className="block text-xs text-gray-400 mt-0.5">
                Optional. Delivery is queued only after the server-side outbox rollout is enabled.
              </span>
            </span>
          </label>

          {/* Send button */}
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canSend || sending}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl
              text-sm font-bold text-white transition-all active:scale-95
              disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#085041' }}
            onMouseEnter={(e) => {
              if (canSend && !sending) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#063830';
            }}
            onMouseLeave={(e) => {
              if (canSend && !sending) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#085041';
            }}
          >
            {sending ? (
              <>
                <Spinner />
                Sending...
              </>
            ) : (
              <>
                <span>🔔</span>
                Send Broadcast
              </>
            )}
          </button>
        </div>

        {/* RIGHT — Preview */}
        <div className="space-y-4">
          <PreviewCard title={title} body={body} />

          {/* Target summary */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Selected Target</p>
            <div className="flex items-center gap-2">
              <span className="text-lg">{target === 'all' ? '📢' : target === 'sellers' ? '🏪' : '🆕'}</span>
              <span className="text-sm font-semibold text-gray-700">{TARGET_LABELS[target]}</span>
            </div>
            <p className="text-xs text-gray-400">
              {target === 'all' && 'All non-suspended users will receive this notification.'}
              {target === 'sellers' && 'Only users with an active seller account will receive this.'}
              {target === 'new_users' && 'Users who joined in the last 30 days will receive this.'}
            </p>
          </div>
        </div>
      </div>

      {/* Confirm dialog */}
      <ConfirmDialog
        open={showConfirm}
        target={target}
        sending={sending}
        onConfirm={handleSend}
        onCancel={() => { if (!sending) setShowConfirm(false); }}
      />

      {/* Toasts */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default Notifications;
